import React, { useEffect, useMemo, useState } from 'react';
import AdminHeader from './AdminHeader';
import Pagination from '../common/Pagination';
import { CheckCircle, XCircle, Clock,ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApprovalBusiness, PutApprovalBusiness } from '../../services/api';
const ITEMS_PER_PAGE = 10;


const AdminApproval = ( showBack = true) => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('requestDate');
  const [sortDir, setSortDir] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
   const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const resp = await getApprovalBusiness({});
        const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);
        const data = list.map((it, idx) => ({
          id: it.id || it._id || String(idx),
          approvalId: it._id || it.id || null,
          businessName: it.Name || (it.firstName ? `${it.firstName} ${it.lastName || ''}`.trim() : (it.name || (it.businessEmail?.split?.('@')[0] || 'Business'))),
          businessEmail: it.businessEmail || it.email || '',
          points: Number(it.points ?? it.point ?? 0),
          amount: Number(it.amount ?? 0),
          note: it.note || '',
          status: (it.status || 'pending').toLowerCase(),
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
        setRequests(data);
        setFiltered(data);
      } catch (e) {
        setRequests([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAction = async (id, action) => {
    // Optimistic update
    const prevRequests = requests;
    const prevFiltered = filtered;
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: action } : r)));
    setFiltered((prev) => prev.map((r) => (r.id === id ? { ...r, status: action } : r)));

    try {
      const target = requests.find(r => r.id === id);

      await PutApprovalBusiness(
        id, // Pass the _id for URL
        {
          status: action,         // Pass only the payload fields here
        }
      );
      // Refresh from server to ensure consistency
      const resp = await getApprovalBusiness({});
      const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);
      const data = list.map((it, idx) => ({
        id: it.id || it._id || String(idx),
        approvalId: it._id || it.id || null,
        businessName: it.Name || (it.firstName ? `${it.firstName} ${it.lastName || ''}`.trim() : (it.name || (it.businessEmail?.split?.('@')[0] || 'Business'))),
        businessEmail: it.businessEmail || it.email || '',
        points: Number(it.points ?? it.point ?? 0),
        amount: Number(it.amount ?? 0),
        note: it.note || '',
        status: (it.status || 'pending').toLowerCase(),
        requestDate: it.requestDate || it.created_on || new Date().toISOString(),
      }));
      setRequests(data);
      setFiltered(data);
    } catch (e) {
      // Rollback on failure
      setRequests(prevRequests);
      setFiltered(prevFiltered);
    }
  };

  const handleBack = () => {
    // if (onBack) return onBack();
    navigate('/admin/dashboard');
  };
  useEffect(() => {
    let data = [...requests];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((r) => (r.businessName || '').toLowerCase().includes(q) || (r.businessEmail || '').toLowerCase().includes(q));
    }
    if (status !== 'all') {
      data = data.filter((r) => r.status === status);
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      data = data.filter((r) => new Date(r.requestDate) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      data = data.filter((r) => new Date(r.requestDate) <= to);
    }
    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (sortBy === 'requestDate') return (new Date(av) - new Date(bv)) * dir;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (Number(av) - Number(bv)) * dir;
    });
    setFiltered(data);
    setCurrentPage(1);
  }, [search, status, dateFrom, dateTo, sortBy, sortDir, requests]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const { pageItems, totalPages } = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return {
      pageItems: filtered.slice(start, end),
      totalPages: Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE)),
    };
  }, [filtered, currentPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader  />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div>   
        {showBack && (
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-black px-3 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
        </div>
        <h1 className="text-2xl font-bold mb-6">Points Approval</h1>
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business or email..."
              className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-2"
            />
            <div>
              <label className="block text-xs text-gray-600 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <button onClick={() => { setSearch(''); setStatus('all'); setDateFrom(''); setDateTo(''); }} className="self-end bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={() => toggleSort('businessName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Business {sortBy==='businessName' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={() => toggleSort('businessEmail')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Email {sortBy==='businessEmail' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={() => toggleSort('points')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Points {sortBy==='points' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={() => toggleSort('amount')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Amount {sortBy==='amount' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={() => toggleSort('requestDate')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Requested {sortBy==='requestDate' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={() => toggleSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Status {sortBy==='status' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={7}>Loading...</td></tr>
                ) : pageItems.length === 0 ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={7}>No requests found</td></tr>
                ) : (
                  pageItems.map((r) => (
                    <tr key={r.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.businessName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.businessEmail}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(r.points || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{Number(r.amount || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(r.requestDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : r.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {r.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                          {r.status === 'approved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                          {r.status === 'rejected' && <XCircle className="w-3 h-3 inline mr-1" />}
                          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {r.status === 'pending' ? (
                          <div className="flex space-x-2">
                            <button onClick={() => handleAction(r.id, 'approved')} className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700">Approve</button>
                            <button onClick={() => handleAction(r.id, 'rejected')} className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700">Reject</button>
                          </div>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)))}
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={filtered.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminApproval;
